import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, FormGroup, FormControl, HelpBlock } from 'react-bootstrap';

/**
 * @typedef {import('../../containers/Pages/TagManager/types').RenameTagDialogProps} RenameTagDialogProps
 */

/**
 * The local UI state of the rename dialog.
 *
 * @typedef {Object} RenameTagDialogState
 * @property {string} tag - The new tag input value.
 * @property {string|null} error - The validation error message, or null.
 * @property {boolean} showError - Whether the error is visible.
 */

/**
 * Rename Tag Dialog
 * -----------------------------------------------------
 * Presents a modal with a text input for a new tag label. The dialog
 * validates that the label is non-empty and different from the current
 * label, then fires onSuccess with the trimmed label.
 */
export default class RenameTagDialog extends Component {
  /**
   * Returns the initial dialog state.
   *
   * @returns {RenameTagDialogState} The initial state.
   */
  getInitialState() {
    return {
      tag: '',
      error: null,
      showError: false,
    };
  }

  /** @type {RenameTagDialogState} */
  state = this.getInitialState();

  /**
   * Resets the dialog state when it is hidden.
   *
   * @param {RenameTagDialogProps} prevProps - The previous props.
   * @returns {void}
   */
  componentDidUpdate(prevProps) {
    const { show } = this.props;

    if (prevProps.show && !show) {
      this.setState(this.getInitialState());
    }
  }

  /**
   * Updates the input value and re-validates when an error is showing.
   *
   * @param {React.SyntheticEvent} event - The input change event.
   * @returns {void}
   */
  onChange = event => {
    const { showError } = this.state;

    this.setState({ tag: event.target.value });

    if (showError) {
      this.validate(event.target.value);
    }
  };

  /**
   * Submits the rename when the input is valid.
   *
   * @returns {void}
   */
  rename = () => {
    const { onSuccess } = this.props;
    const { tag } = this.state;

    if (this.validate()) {
      onSuccess(tag.trim());
    }
  };

  /**
   * Returns the bootstrap validation state for the input.
   *
   * @param {string|null} error - The current validation error.
   * @returns {string|null} 'error' when there is an error, else null.
   */
  errorClass = error => (error ? 'error' : null);

  /**
   * Validates the new tag label and updates the error state.
   *
   * @param {string} [value] - The value to validate; falls back to state.
   * @returns {boolean} True when the label is valid.
   */
  validate = value => {
    const { tag: stateTag } = this.state;
    const { currentTag } = this.props;
    const newTag = value || stateTag;
    let error = null;

    if (!newTag.trim()) {
      error = 'This field cannot be empty.';
    } else if (newTag.trim().toLowerCase() === currentTag.toLowerCase()) {
      error = 'This is already the tag name.';
    }

    this.setState({ showError: true, error });

    return !error;
  };

  /**
   * Renders the rename dialog modal.
   *
   * @returns {JSX.Element} The modal markup.
   */
  render() {
    const { currentTag, show, onHide } = this.props;
    const { tag, error } = this.state;

    return (
      <Modal dialogClassName="tagmanager-dialog" backdrop="static" show={show} onHide={onHide}>
        <Modal.Header>
          <Modal.Title data-test="modal-renametag-title">Rename Tag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Rename <b>{currentTag}</b>. Enter a new name for the tag.
          </p>
          <FormGroup validationState={this.errorClass(error)}>
            <FormControl
              data-test="modal-renametag-input-name"
              name="tag"
              placeholder="Enter a new name"
              value={tag}
              onChange={this.onChange}
              autoFocus
            />
            <HelpBlock>{error}</HelpBlock>
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-cancel" onClick={onHide}>
            Cancel
          </Button>
          <Button
            data-test="modal-renametag-button-rename"
            className="btn-add"
            bsStyle="primary"
            onClick={this.rename}
          >
            Rename Tag
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

RenameTagDialog.propTypes = {
  currentTag: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
